/**
 * New inquiry — a FULL PAGE at `/countries/:cc/inquiries/new`, built to
 * `forms.html` screen 1 (WEB TIER A · WT5; owner rulings: forms are full
 * pages AND the slide-over is killed entirely).
 *
 * ===========================================================================
 * WHAT MOVED FROM THE PANEL, AND WHAT THE PAGE SAYS THAT THE PANEL COULD NOT
 * ===========================================================================
 * The field set is `NewInquiryPanel`'s — the rest of `CreateInquiryDto`, which
 * is the set the endpoint actually accepts (party, subject, details; the panel's
 * header records why Figma's seven no-column fields are not built, and that
 * reasoning is unchanged). ⚠ THAT SET SHRANK TWICE, AND BOTH TIMES BECAUSE A
 * COLUMN WENT: `channel` at migration 43 and `expected value` + `currency` at
 * migration 44 (owner rulings Q5 and Q6). ⚠ AND IT GREW TWICE, FOR THE MIRROR
 * REASON: `tally_serial` / `tally_edition` / `tally_release` arrived at
 * migration 45 (owner ruling Q9), and `platforms` — the design's REQUIREMENTS
 * group, drawn from the shared vocabulary — arrived at migration 48 (owner ruling
 * 5, IQ-9 / D19; see "REQUIREMENTS" below), so the page is four cards. The set
 * tracks the endpoint in both directions — a form offering a control the API
 * would 400 is the failure this file exists to avoid, and a column with no
 * control is a field the coordinator cannot fill. What the
 * page ADDS is the mockup's "two things the drawer had no room to say": the
 * aside's "What Create inquiry does" (one row, one number, one event, nothing
 * else moves) and "If they are not in the list" (the prospect ROUTE). The
 * numbered cards carry the record's lifecycle grammar — ① complete with its
 * own Change action, ② current with a left edge, ③ optional with a dashed rim
 * at full contrast. Nothing on the page is dimmed.
 *
 * ===========================================================================
 * ⚠ IQ-8 — THE LICENCE REGISTER SITS UNDER STEP ①, AND ADDS NO FIELD
 * ===========================================================================
 * A READ-ONLY panel between ① and ② shows what the licence register holds under
 * the chosen customer's NAME (owner ruling round-1 Q1). It adds no column, no
 * route and no control: it neither writes to the register nor fills step ③,
 * whose fields are a CLAIM and must stay one — see `LicenceRegisterPanel`'s own
 * header for that reasoning, for why it is ABSENT rather than empty for a seat
 * without `quotes.view`, and for why every row is labelled a name match.
 *
 * ===========================================================================
 * ⚠ REQUIREMENTS — THE DESIGN'S 5-CHECKBOX GROUP, BUILT FROM THE SHARED LIST (IQ-9 / D19)
 * ===========================================================================
 * `VISUAL-HONESTY.md` §9 filed *"`REQUIREMENTS` 5-checkbox group — not built | BY
 * SCHEMA — same missing-modules cause as §5/§6"*, and that was true: `inquiries` had no
 * modules column and no join table. Migration 48 gives it one — `inquiry_platforms`,
 * `crf_platforms`' exact shape — over the ONE shared platform vocabulary (owner ruling 5:
 * *"the third word is `inquiry`. One list, three applicability words … A row may carry
 * any combination"*). So the group is buildable and is built, as StepCard ④:
 *
 *   · THE OPTIONS COME FROM THE SERVER (`useInquiryPlatformOptions` →
 *     `GET /countries/:cc/inquiries/platforms`). Figma draws five fixed boxes; the build
 *     draws however many values the owner's list holds for THIS country today, by name,
 *     in the vocabulary's order. There is NO list in this file — the day somebody adds
 *     `const REQUIREMENTS = [...]` here to save a fetch, the mirror the owner named is
 *     back (`features/crfs/domain.ts` header has the ruling in full).
 *   · IT IS THE CRF PAGE'S CUSTOMIZATION IDIOM, TRANSCRIBED (`CrfCreatePage`): a checkbox
 *     per offered value, a details textarea that appears when it is ticked and whose text
 *     SURVIVES an untick (an accidental click must not eat a paragraph), and a payload
 *     built from the CHECKED set only.
 *   · OMITTED WHEN NOTHING IS TICKED — this page's own convention (the Tally claim is
 *     sent the same way): on a create, an absent key and `[]` say the same thing to the
 *     server, and this page sends what was given.
 *   · THE AFFORDANCE, NOT THE GUARD. What is offered here is what the server says may be
 *     newly attached in this country; a stale screen offering a value the owner has since
 *     retired is refused on write with a 422 naming it, and the page shows that sentence.
 *
 * ===========================================================================
 * THE PROSPECT CREATE IS A ROUTE, NOT A FORM INSIDE A FORM
 * ===========================================================================
 * The panel's picker carried an inline three-field prospect mini-form. The
 * mockup's aside kills it: "That is a route, not a form inside a form: it
 * opens New customer and returns here with the customer chosen and this page
 * still filled in." So the picker's create action NAVIGATES to
 * `/countries/:cc/parties/new` with router state:
 *
 *     { returnTo: <this page's path>, name: <what was typed>, draft: {…} }
 *
 * and this page, on mount, restores `location.state.draft` and accepts
 * `location.state.party` as the chosen customer. That is the WT5 half of the
 * `?return=` contract; the WT6 PartyCreatePage honours it by navigating back
 * to `returnTo` with `{ party, draft }`. Until WT6 lands, the destination is
 * the shell's not-found state — flagged in the cluster report, by design
 * rather than by accident (building a second inline form here would have
 * rebuilt the thing the ruling killed).
 *
 * ===========================================================================
 * "AUTOSAVED" IS STILL NOT RENDERED, BECAUSE NOTHING IS STILL SAVED
 * ===========================================================================
 * The panel carried a four-paragraph TODO refusing to print an autosave label
 * over storage that does not exist. The page inherits the refusal and states
 * the true fact instead, permanently, in the ReadyBar: "Nothing is saved
 * until the action above is pressed." The aside adds the panel's other
 * honesty: there is no draft storage, and Cancel means the number is never
 * issued.
 *
 * THE PICKER IS THE VENDORED COMBOBOX SINCE WT6 (ledger item 14 discharged):
 * the async enabler (`onQueryChange` + `serverFiltered`) landed with the
 * PartyPicker migration and both party pickers adopted it at once, as the
 * filing said they would. The SERVER search underneath is unchanged
 * (`GET /parties?q=`, two-character floor); the whole state ladder — type two
 * characters / Searching… / the route's refusal / no match — speaks through
 * the Combobox's one honest empty slot, and the create action is the same
 * ROUTE hand-off as before, now the listbox's footer row.
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Combobox,
  CountryBadge,
  KvList,
  KvRow,
  PageHead,
  ReadyBar,
  RecordCard,
  SelectField,
  SkeletonRows,
  StepCard,
  TextAreaField,
  TextField,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { countryProfile } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import { createErrorText } from './errors';
import { TALLY_EDITION_LABELS } from './format';
import { useCreateInquiry, useInquiryPlatformOptions, usePartySearch } from './use-inquiries';
import type { InquiryPlatformEntry, PartyDto } from './api';
import type { TallyEdition } from '../../lib/inquiries-api';
/*
 * ⚠ THE REGISTER'S WIRE LAYER IS IMPORTED, NOT RE-WRITTEN (IQ-8). Every type and
 * every read below comes from `features/tally-licences/api.ts` UNCHANGED — no new
 * route, no widened filter, no second transcription of the payload. If this panel
 * ever needs a field the register does not publish, that is a FINDING to report,
 * not a licence to widen the route from the screen that wanted it.
 */
import {
  fetchLicenceCustomers,
  fetchLicenceRegister,
  licenceErrorText,
  licenceKeys,
} from '../tally-licences/api';
import { useToast } from './toast';

/*
 * ⚠ `MONEY_PATTERN` STOOD HERE AND IS DELETED WITH THE FIELD IT GUARDED. It was
 * `/^\d{1,12}(\.\d{1,2})?$/` — `expected_value` is `DECIMAL(14,2)` and the API REJECTS
 * over-scale input rather than rounding it, so the client refused the same shape locally
 * instead of letting a 422 be the first thing the operator heard. Migration 44 drops the
 * column (owner ruling Q6); there is no figure on this form to validate.
 *
 * ⚠ THE SERVER-SIDE HALF WENT TOO, and that is the point of saying so here: the pattern
 * was a MIRROR of `assertDecimalString(…, MONEY_14_2)` in `inquiries.service.ts`, and both
 * sides are removed in the same commit. A client pattern outliving its server rule is a
 * D-1-shaped defect (a client claiming a parity the server no longer holds), which this
 * branch has already fixed once.
 */

/**
 * The WT5 half of the parties/new round-trip contract (header, §route).
 *
 * ⚠ `channel` LEFT THIS SHAPE WITH THE COLUMN (migration 43, owner ruling Q5), and `value`
 * + `currency` left it at migration 44 (Q6). It matters here and not only on the form:
 * this object is what survives the trip to New customer and back, so a key left in it
 * would be a field the page restores into a control that no longer exists.
 */
interface CreateDraft {
  subject: string;
  details: string;
  /*
   * ⚠ THE TALLY CLAIM TRAVELS TOO (migration 45, owner ruling Q9), and the reason is the
   * mirror of the reason `channel` had to LEAVE this shape. A key left here after its
   * control went would be restored into nothing; a key MISSING here while its control
   * exists is a field the operator typed and the detour silently emptied. The page's
   * promise is "returns here with this page still filled in", and a draft is the only
   * thing that can keep it.
   */
  tally_serial: string;
  tally_edition: string;
  tally_release: string;
  /*
   * ⚠ THE REQUIREMENTS TRAVEL TOO (migration 48, owner ruling 5; IQ-9 / D19), for the same
   * reason as the three above: a coordinator who ticked two values and typed a paragraph
   * of scope, then went to create the customer, must come back to both. Keyed by platform
   * id — the ONLY thing this page holds about a value; the names are re-fetched with the
   * picker on return.
   */
  platform_on: Record<string, boolean>;
  platform_detail: Record<string, string>;
}

interface HandOffState {
  party?: PartyDto;
  draft?: Partial<CreateDraft>;
}

/* Tone pills — the mockup's `tone()` chips on the card headers. Tint + word,
 * never colour alone, same vocabulary as the status pills. */
function OkPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-ok-bg px-2.5 py-0.5 text-label font-medium text-ok-fg">
      {children}
    </span>
  );
}
function InfoPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-info-bg px-2.5 py-0.5 text-label font-medium text-info-fg">
      {children}
    </span>
  );
}
function NeutralPill({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-0.5 text-label font-medium text-fg-secondary ring-1 ring-line">
      {children}
    </span>
  );
}

/** forms.html's `help` line — text/secondary INSIDE a card (the 4.64 pair). */
function Help({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="text-label text-fg-secondary">{children}</p>;
}

export function InquiryCreatePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, me } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const profile = countryProfile(countryCode);
  const handOff = (location.state ?? null) as HandOffState | null;

  const [party, setParty] = useState<PartyDto | null>(handOff?.party ?? null);
  const [subject, setSubject] = useState(handOff?.draft?.subject ?? '');
  const [details, setDetails] = useState(handOff?.draft?.details ?? '');
  /* The Tally claim — migration 45, owner ruling Q9. `''` is "not stated", for all three;
   * the submit below omits the key rather than sending a blank. */
  const [tallySerial, setTallySerial] = useState(handOff?.draft?.tally_serial ?? '');
  const [tallyEdition, setTallyEdition] = useState(handOff?.draft?.tally_edition ?? '');
  const [tallyRelease, setTallyRelease] = useState(handOff?.draft?.tally_release ?? '');
  /* REQUIREMENTS (header §REQUIREMENTS) — the CRF page's two-record idiom: which values are
   * ticked drives the payload; the detail text per value is KEPT across untick/re-tick. */
  const [platformOn, setPlatformOn] = useState<Readonly<Record<string, boolean>>>(
    handOff?.draft?.platform_on ?? {},
  );
  const [platformDetail, setPlatformDetail] = useState<Readonly<Record<string, string>>>(
    handOff?.draft?.platform_detail ?? {},
  );

  const create = useCreateInquiry(countryCode);
  const canCreate = can(countryCode, 'inquiries.create');
  /* Declared before the permission early-return (hook order); disabled for a seat that
   * cannot create, so no request is prepared for a page that will not render the picker. */
  const platformOptions = useInquiryPlatformOptions(countryCode, canCreate);

  const listHref = `/countries/${countryCode}/inquiries`;

  if (!canCreate) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Creating inquiries is not available to this account">
          Raising an inquiry in {countryCode} needs the Inquiries create permission. Ask an
          administrator to widen your access.
        </Alert>
        <Link to={listHref} className="w-fit text-body13 text-indigo hover:underline">
          Back to the pipeline
        </Link>
      </div>
    );
  }

  /*
   * ⚠ `moneyError` STOOD HERE AND IS GONE WITH THE FIELD. It was the ONLY way this form
   * could be invalid other than "no customer", which is why `canSubmit` is now a
   * two-clause predicate rather than three.
   */
  const canSubmit = party !== null && !create.isPending;

  /**
   * The CHECKED set, in the picker's (= the vocabulary's) order, with each value's detail
   * trimmed to `null` when blank. Built from `platformOptions.data` rather than from
   * `platformOn` alone so a tick that outlived its option (a value retired between two
   * renders) is not sent — the server would refuse it by name anyway; this keeps the
   * payload to what the screen actually shows.
   */
  const chosenPlatforms = (): InquiryPlatformEntry[] =>
    (platformOptions.data ?? [])
      .filter((option) => platformOn[option.id] === true)
      .map((option) => {
        const detail = (platformDetail[option.id] ?? '').trim();
        return { platform_id: option.id, detail: detail === '' ? null : detail };
      });

  const submit = (): void => {
    if (party === null || !canSubmit) return;
    const platforms = chosenPlatforms();
    create.mutate(
      {
        party_id: party.id,
        ...(subject.trim() === '' ? {} : { subject: subject.trim() }),
        ...(details.trim() === '' ? {} : { details: details.trim() }),
        /* OMITTED WHEN NOTHING IS TICKED — header §REQUIREMENTS. */
        ...(platforms.length === 0 ? {} : { platforms }),
        /*
         * OMITTED WHEN BLANK, never sent empty — the same rule the two above follow, and
         * here it carries a meaning as well as a convention: an empty box is not a claim.
         * A `tally_serial: ''` would record that the coordinator asked and was told
         * nothing, which is a different fact from not having asked. (`forbidNonWhitelisted`
         * would accept the key either way; this is about what the row says, not about
         * whether the request is legal.)
         */
        ...(tallySerial.trim() === '' ? {} : { tally_serial: tallySerial.trim() }),
        ...(tallyEdition === '' ? {} : { tally_edition: tallyEdition as TallyEdition }),
        ...(tallyRelease.trim() === '' ? {} : { tally_release: tallyRelease.trim() }),
      },
      {
        onSuccess: (record) => {
          toast.push({ text: `${record.doc_no} created` });
          // A routed page has somewhere to go: back to the register the new
          // row now appears on (the toast survives — the routes share one
          // ToastProvider layout, which is that file's documented reason).
          navigate(listHref);
        },
      },
    );
  };

  /** The route hand-off: leave with the draft so it can come back intact. */
  const handOffToPartyCreate = (typedName: string): void => {
    navigate(`/countries/${countryCode}/parties/new`, {
      state: {
        returnTo: location.pathname,
        name: typedName,
        draft: {
          subject,
          details,
          tally_serial: tallySerial,
          tally_edition: tallyEdition,
          tally_release: tallyRelease,
          platform_on: { ...platformOn },
          platform_detail: { ...platformDetail },
        } satisfies CreateDraft,
      },
    });
  };

  const seatRole = me?.scopes.find((scope) => scope.c === countryCode)?.r;
  /*
   * ⚠ `currencyOptions` AND `preview` STOOD HERE AND ARE GONE WITH STEP ③ (migration 44,
   * owner ruling Q6). `currencyOptions` built the CD alternate-currency select from
   * `countries.alt_currency`; `preview` rendered "Renders on the register as ₹18,000"
   * through `formatMoney`. Both are dead because the register has no such column any more.
   * `countries.alt_currency` is untouched and still drives the QUOTE builder.
   */

  const customerState = party === null ? 'now' : 'done';

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHead
        title="New inquiry"
        sub={`Nothing is saved yet. Raising as ${me?.name ?? 'this account'}${
          seatRole === undefined ? '' : `, ${seatRole}`
        } for ${profile.name}.`}
        actions={
          <>
            <Link
              to={listHref}
              className="inline-flex h-9 items-center rounded-sm border border-rim px-3 text-body font-medium text-fg hover:bg-subtle"
            >
              Cancel
            </Link>
            <Button onClick={submit} disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create inquiry'}
            </Button>
          </>
        }
      />

      {create.isError ? <Alert tone="danger">{createErrorText(create.error)}</Alert> : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* ═══ the main column — the three numbered cards ═══════════════════ */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <StepCard
            n={1}
            state={customerState}
            title="Customer"
            badge={party === null ? <InfoPill>Current</InfoPill> : <OkPill>Chosen</OkPill>}
            headerAction={
              party === null ? undefined : (
                <Button variant="ghost" onClick={() => setParty(null)}>
                  Change
                </Button>
              )
            }
          >
            {party === null ? (
              <PartySearch
                countryCode={countryCode}
                onSelect={setParty}
                onCreateProspect={handOffToPartyCreate}
              />
            ) : (
              <div className="flex flex-col gap-1.5">
                <span className="inline-flex h-[22px] w-fit items-center rounded-sm border border-line bg-subtle px-2 font-mono text-mono text-fg">
                  {party.name}
                </span>
                <span className="flex min-w-0 items-center gap-1.5">
                  <CountryBadge code={countryCode} />
                  <span className="min-w-0 truncate text-body13 text-fg-secondary">
                    {party.source === 'prospect' ? 'Prospect' : 'Tally customer'}
                    {party.city === null ? '' : ` · ${party.city}`}
                    {party.phone === null ? '' : ` · ${party.phone}`}
                  </span>
                </span>
              </div>
            )}
          </StepCard>

          {/*
           * ⚠ THE PANEL IS RENDERED ONLY ONCE A CUSTOMER IS CHOSEN, and that is a
           * third kind of absence, distinct from the two the panel itself keeps: it
           * is a statement about the FORM (nothing has been named yet, so there is
           * no name to look up). The lookup is by name and the name arrives with the
           * party, exactly as `PartyLicenceTab` takes `partyName` from its host page
           * rather than fetching a second copy of it.
           */}
          {party === null ? null : (
            <LicenceRegisterPanel countryCode={countryCode} party={party} />
          )}

          <StepCard
            n={2}
            state={party === null ? 'next' : 'now'}
            title="Inquiry"
            badge={party === null ? <NeutralPill>After the customer</NeutralPill> : <InfoPill>Current</InfoPill>}
          >
            {/*
             * ⚠ THE TWO-COLUMN GRID WENT WITH THE `Channel` SELECT, ON PURPOSE.
             * Subject shared this row with "Channel — How the customer reached us", which
             * the owner removed (migration 43, ruling Q5). Leaving `sm:grid-cols-2` here
             * would hold an empty right-hand column open on every wide screen and make
             * Subject a half-width field for no reason a reader could see. Subject is now
             * the same full-width column as Details below it.
             */}
            <div className="flex flex-col gap-1.5">
              <TextField
                label="Subject"
                value={subject}
                maxLength={200}
                placeholder="e.g. Invoice customisation — GST format"
                onChange={(event) => setSubject(event.target.value)}
              />
              <Help>
                Printed on the register row and on the board card. {subject.length} of 200
                characters.
              </Help>
            </div>
            <div className="flex flex-col gap-1.5">
              <TextAreaField
                label="Details"
                rows={4}
                maxLength={20_000}
                value={details}
                placeholder="What did they ask for?"
                onChange={(event) => setDetails(event.target.value)}
              />
              <Help>Stored on the inquiry record. Nothing here reaches a customer document.</Help>
            </div>
          </StepCard>

          <StepCard
            n={3}
            state="next"
            title="Tally details"
            badge={<NeutralPill>Optional</NeutralPill>}
          >
            {/*
             * ⚠ THE LABEL IS THE RULING, NOT DECORATION. Owner ruling Q9: *"It is a claim
             * on a phone call and should read as one."* This card asks for three things
             * the CUSTOMER SAID; nothing here is verified, and the second sentence says
             * what specifically is not done rather than hedging in general. It CANNOT be
             * verified today either: `tally_licence_customers.party_id` is NULL for every
             * row, so there is no path from this customer to a register entry (decision
             * D-2). Writing "we will check this" would be the promise this page exists to
             * avoid making.
             */}
            <Help>
              What the customer told you on the call. It is recorded as their claim and is{' '}
              <strong className="font-medium text-fg">
                not checked against the licence register
              </strong>
              .
            </Help>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <TextField
                  label="Tally serial"
                  value={tallySerial}
                  maxLength={20}
                  placeholder="e.g. 705887531"
                  onChange={(event) => setTallySerial(event.target.value)}
                />
                {/*
                 * NO FORMAT HINT AND NO PATTERN. `tally_licences.serial` is `VARCHAR(20)`
                 * rather than `CHAR(9)` because seven rows of the source workbook carry a
                 * trailing comma; a client that insisted on nine digits would refuse the
                 * very strings the register already holds. `maxLength` matches the column
                 * and is the only limit either side enforces.
                 */}
                <Help>As read out. Not tidied, not checked.</Help>
              </div>

              <SelectField
                label="Edition"
                value={tallyEdition}
                onChange={setTallyEdition}
                hint="Leave as Not stated if they did not say."
                options={[
                  /* The empty option is FIRST and is the default: "not stated" is the
                   * ordinary answer, and a select that opened on `Single-user` would put a
                   * claim in the record that nobody made. */
                  { value: '', label: 'Not stated' },
                  { value: 'single', label: TALLY_EDITION_LABELS.single },
                  { value: 'multi', label: TALLY_EDITION_LABELS.multi },
                ]}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <TextField
                label="Tally release"
                value={tallyRelease}
                maxLength={20}
                placeholder="e.g. TallyPrime 5.0"
                onChange={(event) => setTallyRelease(event.target.value)}
              />
              <Help>
                Free text on purpose — the vendor&apos;s numbering is theirs, and a fixed
                list here would be wrong the next time Tally ships.
              </Help>
            </div>
          </StepCard>

          {/*
           * ④ REQUIREMENTS — Figma's `REQUIREMENTS` checkbox group (`09-new-inquiry-panel`),
           * built from the shared vocabulary; header §REQUIREMENTS has the whole argument.
           * The three states below are the CRF page's, verbatim in intent: loading, the
           * list could not be read (say so, do not render an empty picker that looks like
           * "nothing to choose"), nothing offered in this country (also say so — that is a
           * fact about the list, not about this inquiry), and the checkboxes.
           */}
          <StepCard
            n={4}
            state="next"
            title="Requirements"
            badge={<NeutralPill>Optional</NeutralPill>}
          >
            <Help>
              What the customer is asking for, from the group&apos;s shared list. Tick what
              applies and say what exactly is wanted; the same list is used on CRFs and
              projects.
            </Help>
            <div data-testid="inquiry-requirements" className="flex flex-col gap-3">
              {platformOptions.isPending ? (
                <p className="text-label text-fg-secondary">Loading the list…</p>
              ) : platformOptions.isError ? (
                <p className="text-label text-fg-secondary">
                  The requirements list could not be read, so nothing can be ticked right
                  now. {createErrorText(platformOptions.error)}
                </p>
              ) : (platformOptions.data ?? []).length === 0 ? (
                <p className="text-label text-fg-secondary">
                  Nothing is offered for inquiries in {countryCode} yet. The list is maintained
                  by the group and switched on per country.
                </p>
              ) : (
                (platformOptions.data ?? []).map((option) => {
                  const checked = platformOn[option.id] === true;
                  return (
                    <div key={option.id} className="flex flex-col gap-2">
                      <Checkbox
                        label={option.name}
                        checked={checked}
                        onChange={(event) =>
                          setPlatformOn((current) => ({
                            ...current,
                            [option.id]: event.target.checked,
                          }))
                        }
                      />
                      {checked ? (
                        <TextAreaField
                          label={`${option.name} — details`}
                          rows={3}
                          maxLength={5000}
                          hint="What exactly is wanted. Kept if you untick and re-tick."
                          value={platformDetail[option.id] ?? ''}
                          onChange={(event) =>
                            setPlatformDetail((current) => ({
                              ...current,
                              [option.id]: event.target.value,
                            }))
                          }
                        />
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </StepCard>

          {/*
            ⚠ STEP ③ "VALUE" STOOD HERE AND IS GONE ENTIRELY (migration 44, owner ruling
            Q6). It held two controls and a preview:
              · "Expected value" — `inputMode="decimal"`, validated against MONEY_PATTERN,
                helped by "A forecast, not a price. Nothing here prices anything.";
              · "Currency" — the mockup's INERT bordered value for a single-currency
                country ("Fixed by the country. It is inert rather than dimmed: the rim is
                dashed, the word is at full contrast") and, for CD only, an honest
                `SelectField` built from the schema-real `countries.alt_currency`;
              · "Renders on the register as ₹18,000" through `formatMoney`.

            The CARD goes with the fields rather than being left empty or relabelled. A
            numbered StepCard with nothing in it is worse than one fewer step: the ①②③
            grammar is what the page uses to say how much work is left, and a step that
            asks for nothing would misstate that on every render. The remaining two cards
            keep their numbers, so ③ is not renumbered onto something else.

            ⚠ AMENDED BY IQ-7 — THERE IS A ③ AGAIN, AND IT IS A DIFFERENT CARD. The
            sentence above was written when the page had two steps, and its point was that
            ① and ② must not shuffle up. They have not: `Tally details` (migration 45,
            owner ruling Q9) is a NEW third step that happens to take the next free number,
            not `Value` renumbered or relabelled. Nothing of the deleted card survives in
            it — no figure, no currency, no `formatMoney`, and no `MONEY_PATTERN`. It is
            recorded here rather than in a fresh comment because the only reader who could
            be misled is the one who has just read the paragraph above.

            ⚠ THE INERT-CURRENCY TREATMENT IS NOT LOST. It was the mockup's `forms.html`
            card ③ idiom, and it survives wherever a country fact is shown beside a
            control; only THIS instance goes, because it labelled a field that no longer
            exists. `countries.alt_currency` is untouched and still drives the quote
            builder's currency.
          */}

          <ReadyBar
            statement={
              party === null
                ? 'A customer is required before this can be created.'
                : `Ready to create. A customer is chosen and the country is ${profile.name}.`
            }
          />
        </div>

        {/* ═══ the aside — properties + the two consequence cards ═══════════ */}
        <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
          <RecordCard title="Properties">
            <KvList>
              <KvRow label="Country">
                {countryCode} · {profile.name}
              </KvRow>
              <KvRow label="Currency">{profile.currency}</KvRow>
              <KvRow label="Raised by">{me?.name ?? '—'}</KvRow>
              <KvRow label="Stage on create">New Inquiry</KvRow>
              {/*
               * The mockup prints the stem with the FY digits; the client does
               * not compute fiscal years, so the stem stops at what it KNOWS —
               * the country and the series — and the ellipsis is the honest
               * "issued by the server on create".
               */}
              <KvRow label="Number" mono>
                {countryCode}-INQ/…
              </KvRow>
            </KvList>
          </RecordCard>

          <RecordCard title="What Create inquiry does">
            <p className="text-body13 text-fg-secondary">
              One row in inquiries, numbered from the {countryCode}-INQ series, at stage New
              Inquiry, owned by you. It appears on the inquiries register and in the board New
              lane, and an inquiry.created event is written. Nothing else moves.
            </p>
            <p className="text-body13 text-fg-secondary">
              There is no draft storage. Cancel discards this form and the number is never
              issued.
            </p>
          </RecordCard>

          <RecordCard title="If they are not in the list">
            <p className="text-body13 text-fg-secondary">
              The customer search offers to create a prospect from whatever you typed. That is
              a route, not a form inside a form: it opens New customer and returns here with
              the customer chosen and this page still filled in.
            </p>
          </RecordCard>
        </aside>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── the picker ── */

/**
 * The server-searched customer picker — the vendored `Combobox` in its WT6
 * server mode (header: ledger item 14 discharged). The rows are the server's
 * answer to the query (`serverFiltered` — cmdk must not re-filter them), the
 * empty slot speaks the whole state ladder, and the footer create row is the
 * ROUTE hand-off to `parties/new`, never an inline form.
 */
function PartySearch({
  countryCode,
  onSelect,
  onCreateProspect,
}: {
  countryCode: CountryCode;
  onSelect: (party: PartyDto) => void;
  onCreateProspect: (typedName: string) => void;
}): React.JSX.Element {
  const [term, setTerm] = useState('');
  const search = usePartySearch(countryCode, term, true);
  const results = search.data?.data ?? [];
  const searched = term.trim().length >= 2;

  const emptyText = !searched
    ? 'Type at least two characters to search.'
    : search.isPending
      ? 'Searching…'
      : search.isError
        ? createErrorText(search.error)
        : 'No customer matches that.';

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="party-search" className="text-label font-medium text-fg">
        Customer name
      </label>
      <Combobox
        id="party-search"
        options={results.map((candidate) => ({
          value: candidate.id,
          label: candidate.name,
          sub: `${candidate.source === 'prospect' ? 'Prospect' : 'Tally customer'}${
            candidate.city === null ? '' : ` · ${candidate.city}`
          }`,
        }))}
        value={null}
        onSelect={(id) => {
          const candidate = results.find((row) => row.id === id);
          if (candidate !== undefined) {
            onSelect(candidate);
            setTerm('');
          }
        }}
        listLabel="Customer"
        placeholder="Search customers by name"
        searchPlaceholder="Search customers and prospects"
        emptyText={emptyText}
        serverFiltered
        onQueryChange={setTerm}
        onCreate={(query) => onCreateProspect(query.trim())}
        createLabel={(query) =>
          `Create prospect “${query.trim()}” on the New customer page`
        }
      />
      <Help>
        Someone not in the list is created on the New customer page — this page returns
        filled in, with them chosen.
      </Help>
    </div>
  );
}

/* ───────────────────────────────────── the licence-register panel (IQ-8) ── */

/** The same ceiling `PartyLicenceTab` uses: a lead list, not a report. */
const REGISTER_MATCH_LIMIT = 10;
/** One customer's serials. The register itself is one click away for the rest. */
const REGISTER_LICENCE_LIMIT = 10;

/**
 * What the licence register holds under the chosen customer's NAME — read only,
 * under step ①, built to owner ruling round-1 Q1: *"the import is coming and the
 * control should exist when the data lands"*, and when there is nothing to show
 * the screen SAYS SO PLAINLY rather than rendering an empty control.
 *
 * ===========================================================================
 * ⚠ IT READS. IT DOES NOT PREFILL STEP ③, AND THAT IS DELIBERATE.
 * ===========================================================================
 * The unit is named "the licence-register prefill control", and the one thing it
 * must not do is fill `Tally serial` for the coordinator. Step ③ is labelled a
 * CLAIM — owner ruling Q9, *"it is a claim on a phone call and should read as
 * one"* — and its help text promises the record it is "not checked against the
 * licence register". Copying a register serial into that box would write a
 * register fact into a field whose whole meaning is "what the customer said",
 * and the row would then assert a claim nobody made. So the panel puts the
 * register beside the claim and leaves the typing to the human. What it saves is
 * the lookup, not the keystroke.
 *
 * ===========================================================================
 * ⚠ ABSENT, NOT EMPTY, FOR A SEAT WITHOUT `quotes.view` — AND NOTHING FETCHED
 * ===========================================================================
 * Copied verbatim from `PartyLicenceTab`, because the code already dictates it:
 * this page is gated on `inquiries.create` and both register routes are
 * `@RequirePerm('quotes.view')`, which is a genuinely different key. `Tech
 * Support` holds the first and not the second (`role-matrix.json`), so the seat
 * that raises the most inquiries is exactly the seat that may not read the
 * register — a live case, not a hypothetical. It gets the page WITHOUT the card,
 * because an empty card headed "Licence register" is a statement about the PARTY
 * ("nothing on file for them") and a missing card is a statement about the
 * ACCOUNT, and only the second one is true.
 *
 * The `return null` sits BEFORE the `enabled` flag is read but AFTER the hooks
 * are declared: the queries are declared unconditionally (hook order) and
 * DISABLED for this seat, so no request is prepared, not merely discarded. A
 * request that 403s still went out, and "renders nothing" would not notice.
 *
 * ===========================================================================
 * ⚠ THE LOOKUP IS BY NAME, AND EVERY ROW SAYS SO
 * ===========================================================================
 * `tally_licence_customers.party_id` is NULL for every one of the 439 imported
 * names by design until owner decision D-2 — 224 of them carry no country in the
 * workbook, so the import cannot create parties and refuses to invent them. A
 * `party_id` join would therefore return zero rows STRUCTURALLY rather than
 * incidentally, which is why this panel searches `?q=<name>` and labels the
 * result as what it is. The chip is computed from the payload, not hardcoded:
 * the day a customer really does carry this party's id, that row says `Linked to
 * this party` from the data rather than from a release note.
 *
 * ⚠ NO CLASSIFICATION CHIP, ON PURPOSE. `PartyLicenceTab` renders one from its
 * own `CLASSIFICATION_LABEL` map. Copying that map here would make two copies of
 * one vocabulary — the mirror this repo keeps deleting — and exporting it would
 * mean editing `tally-licences/api.ts`, which this unit reuses UNCHANGED. The
 * register link carries the reader to the screen that owns the word.
 *
 * ⚠ ON DAY ONE THIS PANEL FINDS NOTHING FOR EVERY CUSTOMER, and that is the
 * ruled outcome rather than a defect: no licence batch has been imported
 * anywhere yet (`SELECT COUNT(*) FROM tally_licence_batches` = 0 on the gate
 * database). The empty sentence below is written to be true in exactly that
 * state.
 */
function LicenceRegisterPanel({
  countryCode,
  party,
}: {
  /** `can()` is evaluated PER COUNTRY, so this is not a string. */
  countryCode: CountryCode;
  /** The chosen customer: its NAME is the query, its id is only compared. */
  party: PartyDto;
}): React.JSX.Element | null {
  const { can } = useSession();
  const canReadRegister = can(countryCode, 'quotes.view');
  const term = party.name.trim();
  const matchFilters = { q: term, perPage: REGISTER_MATCH_LIMIT };

  const matches = useQuery({
    queryKey: licenceKeys.customers(countryCode, matchFilters),
    queryFn: ({ signal }) => fetchLicenceCustomers(countryCode, matchFilters, signal),
    enabled: canReadRegister && term !== '',
  });

  const rows = matches.data?.data ?? [];
  /*
   * ⚠ THE SERIALS ARE FETCHED ONLY WHEN THE NAME RESOLVES TO EXACTLY ONE
   * REGISTER CUSTOMER. With two matches there is no honest way to pick one — the
   * register's own posture is that a name is a lead — so the panel lists the
   * candidates and fetches no serials rather than showing the first one's and
   * letting the heading imply it is the customer's.
   */
  /* `?? null` because `noUncheckedIndexedAccess` types `rows[0]` as possibly
   * undefined — and `undefined` and `null` must not be two ways to say "no match". */
  const only = rows.length === 1 ? (rows[0] ?? null) : null;
  /* `?customer=` filters the REGISTER's own `l.customer_id`, never `parties.id`. */
  const licenceFilters = {
    customer: only === null ? '' : only.id,
    perPage: REGISTER_LICENCE_LIMIT,
    sort: 'last_event',
    dir: 'desc',
  };
  const licences = useQuery({
    queryKey: licenceKeys.register(countryCode, licenceFilters),
    queryFn: ({ signal }) => fetchLicenceRegister(countryCode, licenceFilters, signal),
    enabled: canReadRegister && only !== null,
  });

  // See the header: absent, not empty. And after the hooks but before either
  // `enabled` can be true, so this seat prepares no request at all.
  if (!canReadRegister) return null;

  const licenceRows = licences.data?.data ?? [];

  return (
    <RecordCard title="Licence register" sub="Read only">
      <div data-testid="inquiry-licence-panel" className="flex flex-col gap-3">
        {matches.isPending ? (
          <SkeletonRows rows={2} label="Looking for this name in the licence register" />
        ) : matches.isError ? (
          <Alert tone="danger" title="The licence register could not be searched">
            <p>{licenceErrorText(matches.error)}</p>
          </Alert>
        ) : rows.length === 0 ? (
          /*
           * ⚠ THE SENTENCE IS ABOUT THE REGISTER, NOT ABOUT THE CUSTOMER. "This
           * customer has no licences" is a different and unproven claim: the search
           * is a contains-match on the register's own canonical names, so a miss
           * says the REGISTER holds nobody of that name and nothing more.
           */
          <p className="text-label text-fg-secondary">
            The register found no customer of that name. That is a search result — it does
            not say this customer holds nothing, because the register is keyed on the
            workbook’s own canonical names and none of them is linked to a customer here.
          </p>
        ) : (
          <ul className="flex flex-col">
            {rows.map((row) => {
              const linked = row.party_id !== null && row.party_id === party.id;
              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line py-2.5 last:border-b-0"
                >
                  <Link
                    to={`/countries/${countryCode}/licences?customer=${row.id}`}
                    className="text-body font-medium text-indigo underline-offset-2 hover:underline"
                  >
                    {row.canonical_name}
                  </Link>
                  {/*
                   * The owner's partner flag (migration 36), rendered because THIS list
                   * is where a seat about to sell a renewal meets the name: renewals for
                   * a partner's licences are chased THROUGH the partner. The
                   * no-classification-chip rule above does not reach it — that rule
                   * refuses to COPY A VOCABULARY MAP, and a boolean with one literal
                   * label carries no vocabulary. Quiet (default chip, no tone), shown
                   * only when SET, and read-only: no HTTP write for it exists.
                   */}
                  {row.is_partner ? <Chip>Partner</Chip> : null}
                  <Chip className={cn(linked ? 'bg-ok-bg text-ok-fg' : 'bg-warn-bg text-warn-fg')}>
                    {linked ? 'Linked to this party' : 'Name match only'}
                  </Chip>
                  <span className="tnum text-label text-fg-secondary">
                    {String(row.licence_count)}{' '}
                    {row.licence_count === 1 ? 'licence' : 'licences'} ·{' '}
                    {String(row.event_count)} {row.event_count === 1 ? 'event' : 'events'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {only === null ? (
          rows.length > 1 ? (
            <p className="text-label text-fg-secondary">
              More than one register customer answers to this name, so no serials are shown
              here — choosing one of them would be a guess. Open a name above to see its
              licences in the register.
            </p>
          ) : null
        ) : licences.isPending ? (
          <SkeletonRows rows={2} label="Reading that customer’s licences" />
        ) : licences.isError ? (
          <Alert tone="danger" title="That customer’s licences could not be read">
            <p>{licenceErrorText(licences.error)}</p>
          </Alert>
        ) : licenceRows.length === 0 ? (
          <p className="text-label text-fg-secondary">
            That register customer carries no licence rows.
          </p>
        ) : (
          <ul data-testid="inquiry-licence-serials" className="flex flex-col">
            {licenceRows.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line py-2.5 last:border-b-0"
              >
                <span className="font-mono text-mono text-fg">{row.serial}</span>
                <span className="text-label text-fg-secondary">{row.status}</span>
                {row.latest_product_name === null ? null : (
                  <span className="min-w-0 truncate text-label text-fg-secondary">
                    {row.latest_product_name}
                  </span>
                )}
                {/*
                 * ⚠ THE ABSENCE IS RENDERED, NOT PAPERED OVER. `cover_to` is NULL
                 * unless a human typed it — the importer writes none, so
                 * `cover_to_source` is `none` on every imported row — and the
                 * register publishes an ESTIMATE (`last_event_on` + 365 days) only
                 * on its own due-soon worklist, where it is labelled as one. A date
                 * substituted here would read as an operator's fact.
                 */}
                {row.cover_to === null ? (
                  <span className="text-label text-fg-muted">
                    No cover-to date — one is stored only when a person types it
                  </span>
                ) : (
                  <span className="tnum text-label text-fg-secondary">
                    Covered to {row.cover_to}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {/*
         * THE D-2 SENTENCE IS UNCONDITIONAL, because it describes the state of the
         * DATA rather than the state of this screen — the same reason
         * `PartyLicenceTab` prints it whatever its rows say.
         */}
        <p className="text-label text-fg-muted">
          The register keeps its own canonical customer names. None of them is linked to a
          customer record yet — that link is owner decision D-2 — so a row above is a name
          match unless it says otherwise, and nothing here is copied into the Tally details
          below.
        </p>

        {(matches.data?.disclosures ?? []).map((line) => (
          <p key={line} className="text-label text-fg-muted">
            {line}
          </p>
        ))}
      </div>
    </RecordCard>
  );
}
